import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpParams,
  HttpProgressEvent,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@apps/clos-sessions/environments';
import {
  EMPTY,
  Observable,
  TimeoutError,
  concat,
  defer,
  from,
  merge,
  of,
  throwError,
  timer,
} from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  materialize,
  mergeMap,
  scan,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
  timeout,
} from 'rxjs/operators';

export const MULTIPART_UPLOAD_THRESHOLD_BYTES = 64 * 1024 * 1024;
export const MULTIPART_UPLOAD_CONCURRENCY = 3;

const MULTIPART_UPLOAD_ATTEMPTS = 3;
const MULTIPART_RETRY_DELAY_MS = 250;

export type StorjUploadStrategy = 'legacy' | 'multipart';

export type StorjMediaType =
  | 'original'
  | 'preview'
  | 'retouch'
  | 'video'
  | 'video_preview'
  | 'avatar';

export interface MultipartUploadPart {
  part_number: number;
  url: string;
  header: Record<string, string | string[]>;
}

export interface MultipartUploadPlan {
  upload_id: string;
  part_size: number;
  part_count: number;
  parts: MultipartUploadPart[];
  expires_at: string;
  completed_part_numbers?: number[];
}

export interface StorjUploadInput {
  file: File;
  imageName: string;
  type: StorjMediaType;
  roomId?: string;
  userId?: string;
  multipartPlan?: MultipartUploadPlan;
  persistMultipartPlan?: (plan: MultipartUploadPlan | undefined) => Observable<unknown>;
  cancel$?: Observable<unknown>;
}

export type StorjUploadEvent =
  | { type: 'start' }
  | {
      type: 'uploading';
      progress: {
        percent: number;
        bytesUploaded: number;
        totalBytes: number;
      };
    }
  | { type: 'complete' };

interface LegacyUploadIntention {
  url: string;
  header: Record<string, string | string[]>;
}

interface PartUploadProgress {
  partNumber: number;
  bytesUploaded: number;
  completed?: boolean;
}

interface PartProgressState {
  bytesByPart: Record<number, number>;
  event: StorjUploadEvent;
}

interface MultipartPlanState {
  current: MultipartUploadPlan;
}

export class StorjUploadCancelledError extends Error {
  constructor() {
    super('Storj upload cancelled');
    this.name = 'StorjUploadCancelledError';
  }
}

export function selectStorjUploadStrategy(fileSize: number): StorjUploadStrategy {
  return fileSize > MULTIPART_UPLOAD_THRESHOLD_BYTES ? 'multipart' : 'legacy';
}

export function sliceMultipartPart(file: Blob, partSize: number, partNumber: number): Blob {
  if (partSize <= 0 || partNumber <= 0) {
    throw new Error('Multipart part size and number must be positive');
  }

  const start = (partNumber - 1) * partSize;
  const end = Math.min(start + partSize, file.size);

  if (start >= file.size) {
    throw new Error(`Multipart part ${partNumber} starts outside the file`);
  }

  return file.slice(start, end);
}

@Injectable({
  providedIn: 'root',
})
export class StorjUploadService {
  protected readonly baseUrl = `${environment.api.baseUrl}/v1/image`;

  private activeUploads = 0;

  private pendingUploads: (() => void)[] = [];

  constructor(protected http: HttpClient) {}

  upload(input: StorjUploadInput): Observable<StorjUploadEvent> {
    return defer(() => {
      if (input.multipartPlan || selectStorjUploadStrategy(input.file.size) === 'multipart') {
        return this.uploadMultipart(input);
      }

      return this.withCancellation(this.uploadLegacy(input), input.cancel$);
    });
  }

  abortMultipart(uploadId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/multipart/abort`, {
      upload_id: uploadId,
    });
  }

  protected uploadLegacy(input: StorjUploadInput): Observable<StorjUploadEvent> {
    return this.initiateLegacy(input).pipe(
      switchMap(({ url, header }) =>
        concat(
          of<StorjUploadEvent>({ type: 'start' }),
          this.putBytes(url, input.file, header).pipe(
            map((event) => this.toUploadEvent(event, input.file.size)),
          ),
          this.completeLegacy(input).pipe(map(() => ({ type: 'complete' }) as StorjUploadEvent)),
        ),
      ),
    );
  }

  protected uploadMultipart(input: StorjUploadInput): Observable<StorjUploadEvent> {
    // Keep initiation alive until its ID is known so cancellation can abort it.
    const plan$ = defer(() => this.resolveMultipartPlan(input)).pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    const upload$ = plan$.pipe(
      switchMap((plan) => {
        this.validateMultipartPlan(input.file, plan);

        return concat(
          of<StorjUploadEvent>({ type: 'start' }),
          this.uploadMultipartParts(input, plan),
          this.completeMultipart(plan.upload_id).pipe(
            catchError((error: unknown) => {
              // A completed upload may still be replayed after expiry. Only discard
              // its progress when the API confirms it never reached completion.
              if (
                (error instanceof HttpErrorResponse && error.status === 410) ||
                (typeof error === 'object' &&
                  error !== null &&
                  'code' in error &&
                  (error as { code: unknown }).code === 408)
              ) {
                return this.abortMultipart(plan.upload_id).pipe(
                  switchMap(() => this.persistMultipartPlan(input, undefined)),
                  switchMap(() => throwError(() => error)),
                );
              }
              return throwError(() => error);
            }),
            // Keep the acknowledged plan for callers that still need to fetch
            // final metadata. Retrying that step must not initiate over the object.
            map(() => ({ type: 'complete' }) as StorjUploadEvent),
          ),
        );
      }),
    );

    return this.withCancellation(upload$, input.cancel$, () =>
      plan$.pipe(switchMap((plan) => this.abortMultipart(plan.upload_id))),
    );
  }

  protected resolveMultipartPlan(input: StorjUploadInput): Observable<MultipartUploadPlan> {
    const plan = input.multipartPlan;
    if (
      plan &&
      (new Date(plan.expires_at).getTime() > Date.now() ||
        plan.completed_part_numbers?.length === plan.part_count)
    ) {
      return of(plan);
    }

    const clearExpiredPlan$ = plan
      ? this.abortMultipart(plan.upload_id).pipe(
          switchMap(() => this.persistMultipartPlan(input, undefined)),
        )
      : of<unknown>(undefined);

    return clearExpiredPlan$.pipe(
      switchMap(() => this.initiateMultipart(input)),
      switchMap((newPlan) => this.persistMultipartPlan(input, newPlan).pipe(map(() => newPlan))),
    );
  }

  protected uploadMultipartParts(
    input: StorjUploadInput,
    plan: MultipartUploadPlan,
  ): Observable<StorjUploadEvent> {
    const completedParts = new Set(plan.completed_part_numbers ?? []);
    const orderedParts = [...plan.parts]
      .filter((part) => !completedParts.has(part.part_number))
      .sort((a, b) => a.part_number - b.part_number);
    const planState = { current: plan };
    const initialBytesByPart: Record<number, number> = {};
    completedParts.forEach((partNumber) => {
      initialBytesByPart[partNumber] = sliceMultipartPart(
        input.file,
        plan.part_size,
        partNumber,
      ).size;
    });

    return from(orderedParts).pipe(
      mergeMap(
        (part) => this.uploadPartWithRetry(input, planState, part),
        MULTIPART_UPLOAD_CONCURRENCY,
      ),
      // Serialize IndexedDB writes so a slower write cannot erase later progress.
      concatMap((progress) => {
        if (!progress.completed) {
          return of(progress);
        }
        completedParts.add(progress.partNumber);
        planState.current = {
          ...planState.current,
          completed_part_numbers: [...completedParts].sort((a, b) => a - b),
        };
        return this.persistMultipartPlan(input, planState.current).pipe(map(() => progress));
      }),
      scan<PartUploadProgress, PartProgressState>(
        (state, progress) => {
          const bytesByPart = {
            ...state.bytesByPart,
            [progress.partNumber]: progress.bytesUploaded,
          };
          const bytesUploaded = Math.min(
            input.file.size,
            Object.values(bytesByPart).reduce((total, bytes) => total + bytes, 0),
          );

          return {
            bytesByPart,
            event: {
              type: 'uploading',
              progress: {
                percent: Math.round((bytesUploaded / input.file.size) * 100),
                bytesUploaded,
                totalBytes: input.file.size,
              },
            },
          };
        },
        {
          bytesByPart: initialBytesByPart,
          event: {
            type: 'uploading',
            progress: {
              percent: 0,
              bytesUploaded: 0,
              totalBytes: input.file.size,
            },
          },
        },
      ),
      map(({ event }) => event),
      distinctUntilChanged((previous, current) => {
        return (
          previous.type === 'uploading' &&
          current.type === 'uploading' &&
          previous.progress.bytesUploaded === current.progress.bytesUploaded
        );
      }),
    );
  }

  protected uploadPartWithRetry(
    input: StorjUploadInput,
    planState: MultipartPlanState,
    part: MultipartUploadPart,
    attempt = 0,
  ): Observable<PartUploadProgress> {
    return defer(() => this.uploadPart(input.file, planState.current, part)).pipe(
      catchError((error: unknown) => {
        const nextAttempt = attempt + 1;
        if (nextAttempt >= MULTIPART_UPLOAD_ATTEMPTS) {
          return throwError(() => error);
        }

        if (this.isSignatureFailure(error)) {
          return this.refreshMultipartPart(planState.current.upload_id, part.part_number).pipe(
            switchMap((refreshedPart) => {
              planState.current = {
                ...planState.current,
                parts: planState.current.parts.map((candidate) =>
                  candidate.part_number === refreshedPart.part_number ? refreshedPart : candidate,
                ),
              };
              // The successful-part write persists this refreshed URL together
              // with progress, avoiding competing full-plan writes.
              return this.uploadPartWithRetry(input, planState, refreshedPart, nextAttempt);
            }),
          );
        }

        if (this.isRetryablePartFailure(error)) {
          return timer(MULTIPART_RETRY_DELAY_MS * 2 ** attempt).pipe(
            switchMap(() => this.uploadPartWithRetry(input, planState, part, nextAttempt)),
          );
        }

        return throwError(() => error);
      }),
    );
  }

  protected uploadPart(
    file: File,
    plan: MultipartUploadPlan,
    part: MultipartUploadPart,
  ): Observable<PartUploadProgress> {
    const bytes = sliceMultipartPart(file, plan.part_size, part.part_number);

    return this.putBytes(part.url, bytes, part.header).pipe(
      map((event) => ({
        partNumber: part.part_number,
        completed: event.type === HttpEventType.Response,
        bytesUploaded:
          event.type === HttpEventType.Response ? bytes.size : Math.min(event.loaded, bytes.size),
      })),
    );
  }

  protected putBytes(
    url: string,
    bytes: Blob,
    header: Record<string, string | string[]>,
  ): Observable<HttpProgressEvent | HttpResponse<string>> {
    return this.withUploadSlot(() =>
      this.http
        .put(url, bytes, {
          headers: this.toBrowserHeaders(header),
          observe: 'events',
          reportProgress: true,
          responseType: 'text',
        })
        .pipe(timeout({ each: 120_000 })),
    ).pipe(
      filter((event: HttpEvent<string>): event is HttpProgressEvent | HttpResponse<string> => {
        return event.type === HttpEventType.UploadProgress || event.type === HttpEventType.Response;
      }),
    );
  }

  protected initiateLegacy(input: StorjUploadInput): Observable<LegacyUploadIntention> {
    return this.http.post<LegacyUploadIntention>(
      `${this.baseUrl}/upload`,
      {},
      { params: this.toLegacyParams(input) },
    );
  }

  protected completeLegacy(input: StorjUploadInput): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/complete`, this.toMediaIdentity(input));
  }

  protected initiateMultipart(input: StorjUploadInput): Observable<MultipartUploadPlan> {
    return this.http.post<MultipartUploadPlan>(`${this.baseUrl}/multipart/initiate`, {
      ...this.toMediaIdentity(input),
      image_size: input.file.size,
      content_type: input.file.type || 'application/octet-stream',
    });
  }

  protected refreshMultipartPart(
    uploadId: string,
    partNumber: number,
  ): Observable<MultipartUploadPart> {
    return this.http.post<MultipartUploadPart>(`${this.baseUrl}/multipart/part`, {
      upload_id: uploadId,
      part_number: partNumber,
    });
  }

  protected completeMultipart(uploadId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/multipart/complete`, {
      upload_id: uploadId,
    });
  }

  protected toLegacyParams(input: StorjUploadInput): HttpParams {
    let params = new HttpParams()
      .set('image_name', input.imageName)
      .set('image_size', String(input.file.size))
      .set('type', input.type);

    if (input.roomId) {
      params = params.set('room_id', input.roomId);
    }
    if (input.userId) {
      params = params.set('user_id', input.userId);
    }

    return params;
  }

  protected toMediaIdentity(input: StorjUploadInput): Record<string, string> {
    const identity: Record<string, string> = {
      image_name: input.imageName,
      type: input.type,
    };

    if (input.roomId) {
      identity.room_id = input.roomId;
    }
    if (input.userId) {
      identity.user_id = input.userId;
    }

    return identity;
  }

  protected toBrowserHeaders(header: Record<string, string | string[]>): HttpHeaders {
    let headers = new HttpHeaders();

    for (const [key, value] of Object.entries(header)) {
      if (key.toLowerCase() === 'content-length') {
        continue;
      }

      const [first] = Array.isArray(value) ? value : [value];
      if (first !== undefined) {
        headers = headers.set(key, first);
      }
    }

    return headers;
  }

  protected toUploadEvent(
    event: HttpProgressEvent | HttpResponse<string>,
    totalBytes: number,
  ): StorjUploadEvent {
    const bytesUploaded =
      event.type === HttpEventType.Response ? totalBytes : Math.min(event.loaded, totalBytes);

    return {
      type: 'uploading',
      progress: {
        percent: Math.round((bytesUploaded / totalBytes) * 100),
        bytesUploaded,
        totalBytes,
      },
    };
  }

  protected validateMultipartPlan(file: File, plan: MultipartUploadPlan): void {
    const expectedPartCount = Math.ceil(file.size / plan.part_size);
    const orderedPartNumbers = [...plan.parts]
      .sort((a, b) => a.part_number - b.part_number)
      .map(({ part_number }) => part_number);

    if (
      !Number.isSafeInteger(plan.part_size) ||
      !Number.isSafeInteger(plan.part_count) ||
      plan.part_size <= 0 ||
      new Set(plan.completed_part_numbers).size !== (plan.completed_part_numbers?.length ?? 0) ||
      plan.completed_part_numbers?.some(
        (part) => !Number.isSafeInteger(part) || part < 1 || part > plan.part_count,
      ) ||
      plan.part_count !== expectedPartCount ||
      plan.parts.length !== plan.part_count ||
      orderedPartNumbers.some((partNumber, index) => partNumber !== index + 1)
    ) {
      throw new Error('Backend returned an invalid multipart upload plan');
    }
  }

  protected persistMultipartPlan(
    input: StorjUploadInput,
    plan: MultipartUploadPlan | undefined,
  ): Observable<unknown> {
    const persisted$ = input.persistMultipartPlan
      ? input.persistMultipartPlan(plan)
      : of(undefined);
    return persisted$.pipe(
      tap(() => {
        input.multipartPlan = plan;
      }),
    );
  }

  protected isSignatureFailure(error: unknown): boolean {
    return error instanceof HttpErrorResponse && [401, 403].includes(error.status);
  }

  protected isRetryablePartFailure(error: unknown): boolean {
    return (
      error instanceof TimeoutError ||
      (error instanceof HttpErrorResponse &&
        ([0, 408, 429].includes(error.status) || error.status >= 500))
    );
  }

  protected withCancellation(
    upload$: Observable<StorjUploadEvent>,
    cancel$?: Observable<unknown>,
    abort?: () => Observable<unknown>,
  ): Observable<StorjUploadEvent> {
    if (!cancel$) {
      return upload$;
    }

    const notifications$ = upload$.pipe(
      materialize(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    const terminal$ = notifications$.pipe(
      filter((notification) => notification.kind !== 'N'),
      take(1),
    );
    const cancellation$ = cancel$.pipe(
      take(1),
      takeUntil(terminal$),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    const uploadEvents$ = notifications$.pipe(
      mergeMap((notification) => {
        if (notification.kind === 'N') {
          return of(notification.value);
        }
        if (notification.kind === 'E') {
          return throwError(() => notification.error);
        }
        return EMPTY;
      }),
      takeUntil(cancellation$),
    );
    const cancellationError$ = cancellation$.pipe(
      switchMap(() => (abort ? abort() : of(undefined))),
      switchMap(() => throwError(() => new StorjUploadCancelledError())),
    );

    return merge(uploadEvents$, cancellationError$);
  }

  private withUploadSlot<T>(request: () => Observable<T>): Observable<T> {
    return new Observable<T>((subscriber) => {
      let active = false;
      const start = () => {
        active = true;
        this.activeUploads += 1;
        // The outer subscriber owns and tears down this HTTP subscription.
        // eslint-disable-next-line rxjs-angular/prefer-takeuntil
        subscriber.add(defer(request).subscribe(subscriber));
      };
      this.pendingUploads.push(start);
      this.drainUploads();
      return () => {
        if (active) {
          this.activeUploads -= 1;
        } else {
          this.pendingUploads = this.pendingUploads.filter((pending) => pending !== start);
        }
        this.drainUploads();
      };
    });
  }

  private drainUploads(): void {
    while (this.activeUploads < MULTIPART_UPLOAD_CONCURRENCY && this.pendingUploads.length) {
      this.pendingUploads.shift()?.();
    }
  }
}
